import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton } from "@/components/DataViews";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";

const MASTER_TYPES = [
  { key: "projects", label: "Projects", extraFields: [] },
  { key: "sites", label: "Sites", extraFields: [] },
  { key: "materials", label: "Materials", extraFields: ["unit", "hsn"] },
  { key: "vendors", label: "Vendors", extraFields: ["gstin", "address", "phone", "email"] },
  { key: "units", label: "Units", extraFields: [] },
  { key: "departments", label: "Departments", extraFields: [] },
  { key: "budget_heads", label: "Budget Heads", extraFields: [] },
  { key: "cost_centres", label: "Cost Centres", extraFields: [] },
];

export default function Masters() {
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("type") || "projects");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", extra: {} });

  const meta = MASTER_TYPES.find((m) => m.key === active);

  const load = () => api.get(`/masters/${active}`).then((r) => setItems(r.data));
  useEffect(() => { load(); setParams({ type: active }); }, [active]);

  const openNew = () => {
    setEditing(null); setForm({ code: "", name: "", description: "", extra: {} }); setOpen(true);
  };
  const openEdit = (r) => { setEditing(r.id); setForm({ code: r.code || "", name: r.name || "", description: r.description || "", extra: r.extra || {} }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.patch(`/masters/${active}/${editing}`, form);
      else await api.post(`/masters/${active}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/masters/${active}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="masters-page">
      <PageHeader
        title="Master Data"
        subtitle="Central catalogues used across all procurement transactions."
        actions={<PrimaryButton onClick={openNew} data-testid="new-master-btn"><Plus size={14} className="mr-2" />New {meta?.label.replace(/s$/, "")}</PrimaryButton>}
      />
      <div className="flex flex-wrap gap-2 mb-4">
        {MASTER_TYPES.map((m) => (
          <button
            key={m.key}
            onClick={() => setActive(m.key)}
            data-testid={`master-tab-${m.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors ${
              active === m.key ? "bg-kabir text-zinc-900" : "bg-secondary hover:bg-secondary/80 text-foreground border border-border"
            }`}
          >
            {m.label} <span className="opacity-60">({active === m.key ? items.length : "…"})</span>
          </button>
        ))}
      </div>

      <DataTable
        testid={`master-${active}-table`}
        columns={[
          { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
          { key: "name", label: "Name" },
          { key: "description", label: "Description", render: (r) => <span className="text-muted-foreground">{r.description}</span> },
          ...(meta?.extraFields || []).map((f) => ({ key: f, label: f.toUpperCase(), render: (r) => <span className="text-xs">{r.extra?.[f] || "-"}</span> })),
          { key: "action", label: "", render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><Edit size={13} /></Button>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(r.id); }}><Trash2 size={13} /></Button>
            </div>
          )},
        ]}
        data={items}
        empty="No records."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {meta?.label.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="master-code" /></div>
            <div><Label className="text-xs uppercase font-semibold">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="master-name" /></div>
            <div className="col-span-2"><Label className="text-xs uppercase font-semibold">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            {(meta?.extraFields || []).map((f) => (
              <div key={f}><Label className="text-xs uppercase font-semibold">{f}</Label><Input value={form.extra?.[f] || ""} onChange={(e) => setForm({ ...form, extra: { ...form.extra, [f]: e.target.value } })} /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={save} data-testid="save-master">Save</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
